import { useEffect, useState } from 'react';
import { AUTO_HEIGHT_MOBILE_EASE } from './AutoHeight';

/** Money-note motion (KAN-1066): opens a little slower than it closes. */
export const NOTE_OPEN_MS = 220;
export const NOTE_CLOSE_MS = 180;

export function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Keeps a collapsible mounted through its exit. `mounted` = render it;
 * `expanded` = the open state to hand to <NoteCollapse open>. Opening mounts
 * collapsed, then expands next frame (so the height tweens); closing collapses,
 * then unmounts when the tween is done (at once under reduced motion).
 */
export function useCollapsePresence(open) {
  const [mounted, setMounted] = useState(open);
  const [expanded, setExpanded] = useState(open);
  useEffect(() => {
    if (open) {
      setMounted(true);
      if (prefersReducedMotion() || typeof requestAnimationFrame !== 'function') { setExpanded(true); return undefined; }
      const raf = requestAnimationFrame(() => setExpanded(true));
      return () => { if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(raf); };
    }
    setExpanded(false);
    if (prefersReducedMotion()) { setMounted(false); return undefined; }
    const t = setTimeout(() => setMounted(false), NOTE_CLOSE_MS);
    return () => clearTimeout(t);
  }, [open]);
  return { mounted: mounted || open, expanded: expanded && open };
}

/**
 * Height + opacity reveal (a 0fr <-> 1fr grid-rows tween, the same fold the
 * kit sections use - no transform, so it is safe inside an AutoHeight). It
 * opens DOWNWARD from its top edge and pushes what is below it. Reduced
 * motion removes the tween, never the layout.
 */
export default function NoteCollapse({ open, children, testId }) {
  const reduced = prefersReducedMotion();
  const ms = open ? NOTE_OPEN_MS : NOTE_CLOSE_MS;
  return (
    <div
      data-testid={testId}
      data-open={open ? 'true' : 'false'}
      style={{
        display: 'grid',
        gridTemplateRows: open ? '1fr' : '0fr',
        opacity: open ? 1 : 0,
        minWidth: 0,
        transition: reduced
          ? 'none'
          : `grid-template-rows ${ms}ms ${AUTO_HEIGHT_MOBILE_EASE}, opacity ${ms}ms ${AUTO_HEIGHT_MOBILE_EASE}`,
      }}
    >
      <div style={{ minHeight: 0, overflow: 'hidden', minWidth: 0 }}>{children}</div>
    </div>
  );
}
